import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Inbox, X } from 'lucide-react';
import { formatCurrency } from '../utils/currency';

/**
 * Payments that arrived against a real unit with no active tenant — usually a
 * deposit paid before the lease was captured. They sit here until assigned.
 */
const UnallocatedPayments = () => {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ count: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get('/api/unallocated-payments');
            setRows(r.data.data || []);
            setSummary(r.data.summary || { count: 0, total: 0 });
        } catch {
            toast.error('Could not load unallocated payments');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // property_id is null when the reference the payer keyed matched no unit
    // at all. Say so plainly rather than rendering "Property null".
    const unitLabel = (p) =>
        [p.building_name, p.unit_name].filter(Boolean).join(' ')
        || p.account_number
        || (p.property_id ? `Property ${p.property_id}` : 'No matching unit');

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-2xl font-bold">Unallocated payments</h1>
                    <p className="text-gray-600">
                        Money received for a unit that had no active tenant at the time. Assign each
                        one to the tenant it belongs to.
                    </p>
                </div>
                {summary.count > 0 && (
                    <div className="text-right">
                        <div className="text-2xl font-bold text-amber-600">{formatCurrency(summary.total)}</div>
                        <div className="text-sm text-gray-500">{summary.count} awaiting assignment</div>
                    </div>
                )}
            </div>

            <div className="bg-white rounded-xl border">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left">
                        <tr>
                            <th className="p-3">Date</th>
                            <th className="p-3">Unit</th>
                            <th className="p-3">Reference</th>
                            <th className="p-3">Paid by</th>
                            <th className="p-3 text-right">Amount</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="p-6 text-center text-gray-500">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={6} className="p-10 text-center text-gray-500">
                                <Inbox className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                Nothing waiting. Every payment received has been assigned to a tenant.
                            </td></tr>
                        ) : rows.map((p) => (
                            <tr key={p.id} className="border-t">
                                <td className="p-3 whitespace-nowrap">{String(p.payment_date).slice(0, 10)}</td>
                                <td className="p-3">
                                    <div className="font-medium">{unitLabel(p)}</div>
                                    <div className="font-mono text-xs text-gray-500">
                                        {p.account_number
                                            || (p.paid_reference ? `keyed: ${p.paid_reference}` : '')}
                                    </div>
                                </td>
                                <td className="p-3 font-mono text-xs">{p.reference_number}</td>
                                <td className="p-3">
                                    <div>{p.payer_name || '—'}</div>
                                    <div className="text-xs text-gray-500">{p.payer_phone || ''}</div>
                                </td>
                                <td className="p-3 text-right font-semibold">{formatCurrency(p.amount)}</td>
                                <td className="p-3 text-right">
                                    <button
                                        onClick={() => setAssigning(p)}
                                        className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm"
                                    >
                                        Assign
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {assigning && (
                <AssignModal
                    payment={assigning}
                    unitLabel={unitLabel(assigning)}
                    onClose={() => setAssigning(null)}
                    onDone={() => { setAssigning(null); load(); }}
                />
            )}
        </div>
    );
};

const AssignModal = ({ payment, unitLabel, onClose, onDone }) => {
    // A payment whose reference matched no unit must be pointed at one before a
    // tenant can be chosen - and when that unit turns out to be vacant, naming
    // the unit IS the whole job: it becomes an ordinary vacant-unit hold.
    const needsUnit = payment.property_id == null;
    const [step, setStep] = useState(needsUnit ? 'unit' : 'tenant');
    const [units, setUnits] = useState([]);
    const [unitQuery, setUnitQuery] = useState('');
    const [unitId, setUnitId] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [tenantId, setTenantId] = useState('');
    const [busy, setBusy] = useState(false);
    const [heading, setHeading] = useState(unitLabel);

    // Debounced, so typing a unit code does not fire a request per keystroke.
    useEffect(() => {
        if (step !== 'unit') return undefined;
        const timer = setTimeout(() => {
            axios.get(`/api/unallocated-payments/${payment.id}/units`,
                { params: unitQuery ? { q: unitQuery } : {} })
                .then((r) => setUnits(r.data.data || []))
                .catch(() => toast.error('Could not load units'));
        }, 250);
        return () => clearTimeout(timer);
    }, [payment.id, step, unitQuery]);

    useEffect(() => {
        if (step !== 'tenant') return;
        axios.get(`/api/unallocated-payments/${payment.id}/candidates`)
            .then((r) => setCandidates(r.data.data || []))
            .catch(() => toast.error('Could not load tenants for this unit'));
    }, [payment.id, step]);

    const setUnit = async () => {
        if (!unitId) return;
        setBusy(true);
        try {
            const r = await axios.post(`/api/unallocated-payments/${payment.id}/unit`,
                { property_id: Number(unitId) });
            setHeading(r.data.unit || r.data.account_number);
            if (r.data.active_tenant) {
                toast.success(`Unit set to ${r.data.unit}`);
                setStep('tenant');
            } else {
                // Nothing more to do: it is now held against a real unit and
                // will be assignable as soon as a lease exists.
                toast.success(`Held against ${r.data.unit} - no active tenant yet`);
                onDone();
            }
        } catch (e) {
            toast.error(e.response?.data?.message || 'Could not set the unit');
        } finally {
            setBusy(false);
        }
    };

    const submit = async () => {
        if (!tenantId) return;
        setBusy(true);
        try {
            const r = await axios.post(`/api/unallocated-payments/${payment.id}/assign`, { tenant_id: Number(tenantId) });
            toast.success(r.data.receipt_number
                ? `Assigned — receipt ${r.data.receipt_number}`
                : 'Payment assigned');
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Could not assign payment');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-4">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-lg font-bold">Assign payment</h2>
                        <p className="text-sm text-gray-600">
                            {formatCurrency(payment.amount)} received for {heading}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400"><X className="h-5 w-5" /></button>
                </div>

                {step === 'unit' ? (
                    <>
                        <p className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            The reference the payer keyed
                            {payment.paid_reference ? ` ("${payment.paid_reference}")` : ''} matched
                            no unit. Choose the unit this payment was meant for.
                        </p>
                        <label className="block">
                            <span className="text-sm font-medium">Search</span>
                            <input
                                value={unitQuery}
                                onChange={(e) => setUnitQuery(e.target.value)}
                                placeholder="Unit code, unit name or building"
                                className="mt-1 w-full border rounded-lg p-2"
                            />
                        </label>
                        <label className="block">
                            <span className="text-sm font-medium">Unit</span>
                            <select
                                value={unitId}
                                onChange={(e) => setUnitId(e.target.value)}
                                size={6}
                                className="mt-1 w-full border rounded-lg p-2"
                            >
                                {units.length === 0 && (
                                    <option value="" disabled>No matching units</option>
                                )}
                                {units.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.account_number || `#${u.id}`}
                                        {` — ${[u.building_name, u.unit_name].filter(Boolean).join(' ')}`}
                                        {u.tenant_id
                                            ? ` — ${`${u.first_name || ''} ${u.last_name || ''}`.trim()}`
                                            : ' — vacant'}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </>
                ) : candidates.length === 0 ? (
                    <p className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        No tenant is on this unit yet. The payment stays here, held against the unit,
                        and can be assigned as soon as the tenant record exists.
                    </p>
                ) : (
                    <label className="block">
                        <span className="text-sm font-medium">Tenant</span>
                        <select
                            value={tenantId}
                            onChange={(e) => setTenantId(e.target.value)}
                            className="mt-1 w-full border rounded-lg p-2"
                        >
                            <option value="">Select a tenant…</option>
                            {candidates.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {`${t.first_name || ''} ${t.last_name || ''}`.trim()}
                                    {t.unit_name || t.account_number
                                        ? ` — ${t.account_number || t.unit_name}` : ''}
                                    {t.status !== 'active' ? ` (${t.status})` : ''}
                                    {t.phone ? ` — ${t.phone}` : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="px-4 py-2 border rounded-lg">Cancel</button>
                    {step === 'unit' ? (
                        <button
                            onClick={setUnit}
                            disabled={!unitId || busy}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                        >
                            {busy ? 'Saving…' : 'Set unit'}
                        </button>
                    ) : (
                        <button
                            onClick={submit}
                            disabled={!tenantId || busy}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                        >
                            {busy ? 'Assigning…' : 'Assign payment'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UnallocatedPayments;
